import React, { useRef, useEffect } from 'react';
import { Card, Empty, Spin } from 'antd';
import Chart from 'chart.js/auto';

const CashFlowChart = ({ data, loading, timePeriod }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Format the date label based on time period
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    
    if (timePeriod === 'daily') {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (timePeriod === 'weekly') {
      // Format: "Week X of YYYY"
      const parts = dateStr.split('-W');
      return `Week ${parts[1]} of ${parts[0]}`;
    } else if (timePeriod === 'monthly') {
      // Format: "Month YYYY"
      const date = new Date(dateStr + '-01');
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else {
      // Yearly
      return dateStr;
    }
  };

  useEffect(() => {
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart if data is available
    if (data && data.length > 0 && chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      
      const labels = data.map(item => formatDateLabel(item.date));
      const budgetedRevenueData = data.map(item => item.revenue);
      const budgetedCostData = data.map(item => item.cost);
      const budgetedProfitData = data.map(item => item.profit);
      const actualRevenueData = data.map(item => item.actual_revenue);
      const actualCostData = data.map(item => item.actual_cost);
      const actualProfitData = data.map(item => item.actual_profit);

      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Budgeted Revenue',
              data: budgetedRevenueData,
              backgroundColor: 'rgba(111, 181, 228, 0.6)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1
            },
            {
              label: 'Actual Revenue',
              data: actualRevenueData,
              backgroundColor: 'rgba(7, 148, 242, 0.94)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
              type: 'line'
            },
            {
              label: 'Budgeted Cost',
              data: budgetedCostData,
              backgroundColor: 'rgba(246, 145, 167, 0.6)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1
            },
            {
              label: 'Actual Cost',
              data: actualCostData,
              backgroundColor: 'rgba(255, 15, 67, 0.9)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
              type: 'line'
            },
            {
              label: 'Budgeted Profit',
              data: budgetedProfitData,
              backgroundColor: 'rgba(118, 222, 222, 0.6)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1
            },
            {
              label: 'Actual Profit',
              data: actualProfitData,
              backgroundColor: 'rgba(4, 137, 137, 0.9)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
              type: 'line'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Amount ($)'
              }
            },
            x: {
              title: {
                display: true,
                text: `Time (${timePeriod})`
              }
            }
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    label += new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(context.parsed.y);
                  }
                  return label;
                }
              }
            },
            legend: {
              position: 'top',
            },
            datalabels: {
              display: false,
            },
            title: {
              display: true,
              text: `Cash Flow (${timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1)})`
            }
          }
        }
      });
    }

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  return (
    <Card title="Cash Flow Chart" style={{ marginBottom: 16 }}>
      <div style={{ height: '400px', position: 'relative' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" />
          </div>
        ) : data && data.length > 0 ? (
          <canvas ref={chartRef} />
        ) : (
          <Empty description="No data available" style={{ marginTop: '100px' }} />
        )}
      </div>
    </Card>
  );
};

export default CashFlowChart;
